'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface WeldingProcess {
  id: number;
  name: string;
  acronym: string;
  description: string;
  applications: string[];
  materials: string[];
  image: string;
  alt: string;
  icon: string;
}

const WeldingProcesses = () => {
  const [activeProcess, setActiveProcess] = useState(0);

  const processes: WeldingProcess[] = [
  {
    id: 1,
    name: "Gas Tungsten Arc Welding",
    acronym: "GTAW/TIG",
    description: "Precision welding process using a non-consumable tungsten electrode to produce the weld. Ideal for thin materials and critical applications requiring contamination-free joints with exceptional quality and control.",
    applications: ["Semiconductor components", "Aerospace parts", "Medical devices", "Thin-wall tubing"],
    materials: ["Stainless steel", "Aluminum", "Titanium", "Nickel alloys"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf88d716-1767949850310.png",
    alt: "Close-up of TIG welding torch creating bright blue arc on stainless steel with precise control and minimal spatter",
    icon: "BoltIcon"
  },
  {
    id: 2,
    name: "Gas Metal Arc Welding",
    acronym: "GMAW/MIG",
    description: "High-productivity welding process using a continuous solid wire electrode fed through a welding gun. Excellent for production environments requiring consistent quality and faster welding speeds on various materials.",
    applications: ["Structural fabrication", "Automotive components", "General manufacturing", "Heavy equipment"],
    materials: ["Carbon steel", "Stainless steel", "Aluminum", "Copper alloys"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1be36bfef-1767508424943.png",
    alt: "Industrial MIG welding operation with bright orange sparks on thick steel plate in manufacturing facility",
    icon: "FireIcon"
  },
  {
    id: 3,
    name: "Flux-Cored Arc Welding",
    acronym: "FCAW",
    description: "Versatile welding process using a tubular wire filled with flux. Combines the productivity of MIG welding with the deep penetration of stick welding, ideal for thick materials and outdoor applications.",
    applications: ["Heavy fabrication", "Shipbuilding", "Pipeline construction", "Structural steel"],
    materials: ["Carbon steel", "Low-alloy steel", "Stainless steel"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b7899ef9-1767379282898.png",
    alt: "Welder performing flux-cored arc welding on heavy steel beam with protective equipment in industrial setting",
    icon: "SparklesIcon"
  },
  {
    id: 4,
    name: "Shielded Metal Arc Welding",
    acronym: "SMAW/Stick",
    description: "Traditional manual welding process using a consumable electrode coated with flux. Highly versatile and portable, suitable for field work and challenging environments where other processes may not be practical.",
    applications: ["Field repairs", "Maintenance work", "Construction", "Heavy equipment repair"],
    materials: ["Carbon steel", "Cast iron", "Stainless steel", "Nickel alloys"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10e750274-1767600847392.png",
    alt: "Experienced welder using stick welding electrode with bright arc and protective face shield on construction site",
    icon: "WrenchScrewdriverIcon"
  }];


  return (
    <section className="py-14 lg:py-22 bg-card">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="CubeIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Welding Capabilities</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Comprehensive Welding Processes
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Master-level expertise across all major welding processes with certified welders and contamination-controlled environments.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-3">
            {processes.map((process, index) =>
            <button
              key={process.id}
              onClick={() => setActiveProcess(index)}
              className={`w-full text-left p-6 rounded-lg border-2 transition-all duration-base ${
              activeProcess === index ?
              'bg-primary text-primary-foreground border-primary shadow-lg' :
              'bg-background text-foreground border-border hover:border-primary/50 hover:shadow-md'}`
              }>

                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                activeProcess === index ? 'bg-white/20' : 'bg-primary/10'}`
                }>
                    <Icon
                    name={process.icon as any}
                    size={24}
                    variant="solid"
                    className={activeProcess === index ? 'text-white' : 'text-primary'} />

                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-headline font-bold mb-1">{process.acronym}</h3>
                    <p className={`text-sm ${activeProcess === index ? 'text-white/90' : 'text-muted-foreground'}`}>
                      {process.name}
                    </p>
                  </div>
                  <Icon
                  name="ChevronRightIcon"
                  size={20}
                  variant="outline"
                  className={`flex-shrink-0 transition-transform duration-base ${
                  activeProcess === index ? 'rotate-90' : ''}`
                  } />

                </div>
              </button>
            )}
          </div>

          <div className="lg:col-span-8">
            <div className="bg-background rounded-lg shadow-lg overflow-hidden border border-border">
              <div className="relative h-80 overflow-hidden">
                <AppImage
                  src={processes[activeProcess].image}
                  alt={processes[activeProcess].alt}
                  className="w-full h-full object-cover" />

                <div className="absolute top-4 left-4 px-4 py-2 bg-primary/90 backdrop-blur-sm rounded-full">
                  <span className="text-sm font-cta font-bold text-white">
                    {processes[activeProcess].acronym}
                  </span>
                </div>
              </div>

              <div className="p-8">
                <h3 className="text-2xl font-headline font-bold text-foreground mb-4">
                  {processes[activeProcess].name}
                </h3>
                <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                  {processes[activeProcess].description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="WrenchIcon" size={20} variant="outline" className="text-primary" />
                      <h4 className="text-sm font-cta font-bold text-foreground uppercase tracking-wide">
                        Applications
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {processes[activeProcess].applications.map((app, idx) =>
                      <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                          <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                          <span>{app}</span>
                        </li>
                      )}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="CubeIcon" size={20} variant="outline" className="text-primary" />
                      <h4 className="text-sm font-cta font-bold text-foreground uppercase tracking-wide">
                        Materials
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {processes[activeProcess].materials.map((material, idx) =>
                      <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                          <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                          <span>{material}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>

                <button className="w-full px-6 py-3 bg-conversion text-conversion-foreground rounded-md text-sm font-cta font-bold hover:opacity-90 transition-all duration-base shadow-sm">
                  Request Process Specifications
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default WeldingProcesses;